import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { EffortAssumption, InitiativeDetailResponse } from '@complan/contracts';
import { hasCapability, useSession } from '@/app/session/session';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { formatDate, formatDateRange } from '@/shared/format/date-format';
import { useEffortAssumptions } from '@/features/manpower/effort-matrix/effort-matrix-data';
import { FF_ROLE_LABEL_KEYS } from '@/shared/i18n/enum-labels';
import type { DetailReferenceData } from './detail-reference-data';
import { useInitiativeWorkload, type WorkloadDriver } from './workload-data';

/**
 * The Detail_Panel's Workload tab: the Field Force calculation chain per driver
 * and the period allocation that reconciles back to the Initiative total
 * (UI spec §7's Workload tab; `complan-manpower` R5.8's transparency disclosure).
 *
 * ## Where each number comes from
 *
 * Three sources, joined on ids, never recomputed here:
 *
 * - **The inputs** — POS_Segment selection, derived population count and its
 *   source timestamp, the manual override and its reason, coverage factor,
 *   frequency and activity dates — come from `detail.fieldForceScope`, which is
 *   what the intake form stored (`complan-manpower` R4.1-R4.7).
 * - **The effort assumption** — effort per unit and fixed overhead — comes from
 *   `GET /api/manpower/effort-assumptions`, matched on the `effortAssumptionId`
 *   the engine reports it used, so the version shown is the version applied
 *   rather than whichever is newest.
 * - **The hours** — per driver, per period and per Platform — come from
 *   `POST /api/manpower/demand`. They are the engine's own output.
 *
 * ## Why no hand-computed intermediate product line
 *
 * The click dummy prints an arithmetic line (`POS × coverage × frequency ×
 * effort ÷ 60`) with a computed subtotal. This tab deliberately shows the same
 * factors but does **not** recompute that product in the browser. `coverageFactor`
 * is stored as a percentage (0.00-100.00), the engine works in exact fractions
 * with defined rounding (Assumption A11), and a second implementation here could
 * print a subtotal that disagrees with the total beneath it by a rounding step —
 * which is precisely the kind of "two numbers, one truth" defect R5.7's
 * Reconciliation_Control exists to catch. Every factor is disclosed and the
 * engine's own `demandHours` is the total; the formula itself is stated as prose.
 */

/** Hours as displayed: at most one decimal, thousands separated (Assumption A11 rounds for display only). */
const hoursFormat = new Intl.NumberFormat('en-GB', { maximumFractionDigits: 1 });
/** A population count as displayed: whole units, thousands separated. */
const countFormat = new Intl.NumberFormat('en-GB', { maximumFractionDigits: 0 });

function formatHours(value: number): string {
  return hoursFormat.format(value);
}

/** A stored decimal-text count (`numeric(12,2)`) as a display count. */
function formatCount(value: string | null): string | null {
  if (value === null) return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? countFormat.format(parsed) : value;
}

export function WorkloadTab({
  detail,
  reference,
}: {
  readonly detail: InitiativeDetailResponse;
  readonly reference: DetailReferenceData;
}): React.JSX.Element {
  const { t } = useTranslation();
  const session = useSession();
  const canRead = hasCapability(session.data, 'manpower:read');

  // Hooks run unconditionally and in a stable order; the capability gates the
  // *request*, not the hook, so hook order is identical on every render while the
  // session query resolves (the pattern the Manpower screens already use).
  const workload = useInitiativeWorkload(detail.id, detail.planningYear, { enabled: canRead });
  const effortAssumptions = useEffortAssumptions();

  const assumptionById = useMemo(() => {
    const map = new Map<string, EffortAssumption>();
    for (const assumption of effortAssumptions.data?.items ?? []) {
      map.set(assumption.id, assumption);
    }
    return map;
  }, [effortAssumptions.data]);

  const driverById = useMemo(() => {
    const map = new Map<string, WorkloadDriver>();
    for (const driver of workload.data?.drivers ?? []) {
      map.set(driver.driverId, driver);
    }
    return map;
  }, [workload.data]);

  // R5.2's "no Field Force involvement contributes no hours" — stated rather than
  // rendered as an empty chain, matching the dummy's info alert.
  if (!detail.fieldForceInvolvement || detail.fieldForceScope.length === 0) {
    return (
      <Alert data-testid="detail-workload-none">
        <AlertDescription>{t('detail.workload.noInvolvement')}</AlertDescription>
      </Alert>
    );
  }

  if (!canRead) {
    return (
      <p className="text-muted-foreground text-[12.3px]" data-testid="detail-workload-no-access">
        {t('detail.workload.noAccess')}
      </p>
    );
  }

  return (
    <div className="space-y-4" data-testid="detail-workload">
      <header>
        <h3 className="text-[13px] font-semibold text-txt">{t('detail.workload.heading')}</h3>
        <p className="text-muted-foreground text-[11px]">{t('detail.workload.hint')}</p>
      </header>

      {workload.isLoading ? (
        <p className="text-muted-foreground text-[12.3px]">{t('detail.workload.loading')}</p>
      ) : workload.isError ? (
        <Alert variant="destructive">
          <AlertDescription>{t('detail.workload.loadError')}</AlertDescription>
        </Alert>
      ) : (
        <>
          {workload.data !== undefined && !workload.data.reconciliationHolds ? (
            <Alert variant="destructive" data-testid="detail-workload-reconciliation">
              <AlertDescription>{t('detail.workload.reconciliationFailed')}</AlertDescription>
            </Alert>
          ) : null}

          {workload.data !== undefined && workload.data.dataIncompleteDriverCount > 0 ? (
            <Alert className="border-warn" data-testid="detail-workload-incomplete">
              <AlertDescription>
                {t('detail.workload.dataIncompleteSummary', {
                  count: workload.data.dataIncompleteDriverCount,
                })}
              </AlertDescription>
            </Alert>
          ) : null}

          {detail.fieldForceScope.map((entry) => (
            <DriverCard
              key={entry.id}
              entry={entry}
              driver={driverById.get(entry.id)}
              assumption={
                driverById.get(entry.id)?.effortAssumptionId !== undefined &&
                driverById.get(entry.id)?.effortAssumptionId !== null
                  ? assumptionById.get(driverById.get(entry.id)!.effortAssumptionId!)
                  : undefined
              }
              reference={reference}
            />
          ))}

          <PeriodAllocation detail={detail} workload={workload.data} />
        </>
      )}
    </div>
  );
}

/**
 * One driver's calculation chain. Takes the amber `.ovr` treatment when the
 * population count is a manual override (R4.3), so the override is visible on the
 * card itself and not only in the row that states it.
 */
function DriverCard({
  entry,
  driver,
  assumption,
  reference,
}: {
  readonly entry: InitiativeDetailResponse['fieldForceScope'][number];
  readonly driver: WorkloadDriver | undefined;
  readonly assumption: EffortAssumption | undefined;
  readonly reference: DetailReferenceData;
}): React.JSX.Element {
  const { t } = useTranslation();
  const none = t('detail.value.none');
  const isOverride = entry.isPopulationOverride;
  const derived = formatCount(entry.derivedPopulationCount);
  const used = formatCount(entry.impactedPopulationCount);
  const isDataIncomplete = driver !== undefined && driver.demandHours === null;

  return (
    <section
      data-testid="detail-workload-driver"
      className={cn(
        'rounded-[4px] border p-3',
        isOverride ? 'border-[#F0DBB4] bg-[#FDF4E4]' : 'border-line bg-surface-alt',
      )}
    >
      <div className="mb-2 flex flex-wrap items-center gap-2">
        <span className="text-[12.3px] font-semibold text-txt">
          {/* Named by activity, since one role can carry several activities. */}
          {t('detail.workload.componentTitleWithActivity', {
            role: t(FF_ROLE_LABEL_KEYS[entry.fieldForceRole]),
            activity: reference.nameOf('ff-activity-type', entry.activityTypeId),
          })}
        </span>
        <Badge variant="outline">{t(FF_ROLE_LABEL_KEYS[entry.fieldForceRole])}</Badge>
        {isOverride ? (
          <span className="rounded-[3px] bg-[#FDF0DC] px-1.5 py-0.5 text-[10px] font-semibold text-[#9A5B00]">
            {t('detail.workload.overrideBadge')}
          </span>
        ) : null}
        {isDataIncomplete ? (
          <Badge variant="secondary">{t('detail.workload.dataIncomplete')}</Badge>
        ) : null}
      </div>

      <dl>
        <CalcLine
          label={t('detail.workload.activityType')}
          value={reference.nameOf('ff-activity-type', entry.activityTypeId)}
        />
        {entry.audienceId !== null ? (
          <CalcLine
            label={t('detail.workload.audience')}
            value={reference.nameOf('audience', entry.audienceId)}
          />
        ) : null}
        {entry.regionId !== null ? (
          <CalcLine
            label={t('detail.workload.region')}
            value={reference.nameOf('region', entry.regionId)}
          />
        ) : null}
        {entry.populationUnitId !== null ? (
          <CalcLine
            label={t('detail.workload.populationUnit')}
            value={reference.nameOf('population-unit', entry.populationUnitId)}
          />
        ) : null}
        {entry.posSegmentIds.length > 0 ? (
          <CalcLine
            label={t('detail.workload.posSegments')}
            value={reference.namesOf('pos-segment', entry.posSegmentIds, none)}
          />
        ) : null}
        {/* R4.3: the derived count is retained and shown *alongside* an override,
            never replaced by it. */}
        {derived !== null ? (
          <CalcLine
            label={
              entry.populationSourceTimestamp === null
                ? t('detail.workload.derivedPopulation')
                : t('detail.workload.derivedPopulationSourced', {
                    at: formatDate(entry.populationSourceTimestamp.slice(0, 10)),
                  })
            }
            value={derived}
            muted={isOverride}
            numeric
          />
        ) : null}
        <CalcLine
          label={
            isOverride
              ? t('detail.workload.populationOverride', {
                  reason: entry.populationOverrideReason ?? none,
                })
              : t('detail.workload.population')
          }
          value={used ?? none}
          emphasis={isOverride}
          numeric
        />
        <CalcLine
          label={t('detail.workload.coverageFactor')}
          value={
            entry.coverageFactor === null
              ? none
              : t('detail.workload.percentValue', { value: entry.coverageFactor })
          }
          numeric
        />
        <CalcLine
          label={t('detail.workload.frequency')}
          value={entry.frequency === null ? none : String(entry.frequency)}
          numeric
        />
        <CalcLine
          label={
            driver?.effortAssumptionVersion == null
              ? t('detail.workload.effortPerUnit')
              : t('detail.workload.effortPerUnitVersioned', {
                  version: driver.effortAssumptionVersion,
                })
          }
          value={
            assumption === undefined
              ? none
              : t('detail.workload.minutesValue', { value: assumption.effortPerUnitMinutes })
          }
          numeric
        />
        <CalcLine
          label={t('detail.workload.fixedOverhead')}
          value={
            assumption?.fixedOverheadHours == null
              ? none
              : t('detail.workload.hoursValue', { value: assumption.fixedOverheadHours })
          }
          numeric
        />
        {entry.activityStartDate !== null && entry.activityEndDate !== null ? (
          <CalcLine
            label={t('detail.workload.activityDates')}
            value={formatDateRange(entry.activityStartDate, entry.activityEndDate)}
          />
        ) : null}
        <CalcLine
          label={t('detail.workload.driverDemand')}
          value={
            driver === undefined
              ? none
              : driver.demandHours === null
                ? t('detail.workload.dataIncomplete')
                : t('detail.workload.hoursValue', { value: formatHours(driver.demandHours) })
          }
          total
          numeric
        />
      </dl>

      {driver !== undefined && driver.platformAllocations.length > 1 ? (
        <div className="mt-2">
          <p className="text-muted-foreground mb-1 text-[11px]">
            {t('detail.workload.platformAllocation')}
          </p>
          <dl>
            {driver.platformAllocations.map((allocation) => (
              <CalcLine
                key={allocation.platformId}
                label={reference.nameOf('platform', allocation.platformId)}
                value={t('detail.workload.hoursValue', { value: formatHours(allocation.hours) })}
                numeric
              />
            ))}
          </dl>
        </div>
      ) : null}
    </section>
  );
}

/**
 * The period allocation, summing back to the Initiative total (R5.6, R5.7).
 *
 * The total row is the engine's `totalDemandHours`, and the per-period rows are
 * the engine's own allocations summed across drivers — so the reconciliation the
 * dummy asserts in words ("reconciles to initiative demand") is something the
 * reader can check on screen rather than take on trust.
 */
function PeriodAllocation({
  detail,
  workload,
}: {
  readonly detail: InitiativeDetailResponse;
  readonly workload: ReturnType<typeof useInitiativeWorkload>['data'];
}): React.JSX.Element | null {
  const { t } = useTranslation();
  if (workload === undefined) return null;

  const byPeriod = new Map<string, number>();
  for (const driver of workload.drivers) {
    for (const allocation of driver.periodAllocations) {
      byPeriod.set(allocation.periodId, (byPeriod.get(allocation.periodId) ?? 0) + allocation.hours);
    }
  }
  // Ordered by the grid the engine returned, so periods read chronologically and a
  // period with no demand simply does not appear.
  const rows = workload.periods
    .map((period) => ({ period, hours: byPeriod.get(period.periodId) ?? 0 }))
    .filter((row) => row.hours > 0);

  if (rows.length === 0) return null;

  return (
    <section className="border-line rounded-[4px] border p-3" data-testid="detail-workload-periods">
      <div className="mb-2 flex flex-wrap items-baseline justify-between gap-2">
        <span className="text-[12.3px] font-semibold text-txt">
          {t('detail.workload.periodAllocation')}
        </span>
        <span className="text-muted-foreground text-[11px]">
          {t('detail.workload.periodAllocationNote')}
        </span>
      </div>
      <dl>
        {rows.map(({ period, hours }) => (
          <CalcLine
            key={period.periodId}
            label={formatDateRange(period.start, period.end)}
            value={t('detail.workload.hoursValue', { value: formatHours(hours) })}
            numeric
          />
        ))}
        <CalcLine
          label={t('detail.workload.totalReconciles', { year: detail.planningYear })}
          value={t('detail.workload.hoursValue', { value: formatHours(workload.totalDemandHours) })}
          total
          numeric
        />
      </dl>
    </section>
  );
}

/**
 * One row of the calculation chain (UI spec's `.calcline`): label left, value
 * right, dashed divider, with a `total` variant carrying a solid top border and
 * bold weight. A `<dl>` row rather than a flex `<div>` so the label/value pairing
 * is conveyed to assistive technology, not only visually.
 */
function CalcLine({
  label,
  value,
  total = false,
  muted = false,
  emphasis = false,
  numeric = false,
}: {
  readonly label: string;
  readonly value: string;
  readonly total?: boolean;
  readonly muted?: boolean;
  readonly emphasis?: boolean;
  readonly numeric?: boolean;
}): React.JSX.Element {
  return (
    <div
      className={cn(
        'grid grid-cols-[1fr_auto] items-baseline gap-2 py-1 text-[12px]',
        total
          ? 'border-line mt-1 border-t pt-1.5 font-semibold'
          : 'border-b border-dashed border-[#E6EBF1]',
      )}
    >
      <dt className={muted ? 'text-muted-foreground' : 'text-txt'}>{label}</dt>
      <dd
        className={cn(
          muted ? 'text-muted-foreground' : 'text-txt',
          emphasis && 'font-semibold',
          numeric && 'tabular-nums',
        )}
      >
        {value}
      </dd>
    </div>
  );
}
